import { type JSX, useCallback, useMemo } from 'react';

import { useOddsStream } from '../../hooks/useOddsStream';
import { shallow, useSelector } from '../../providers/StoreProvider';
import { type OddsStreamUpdate } from '../../types';

interface OddsStreamControllerProps {
  enabled: boolean;
  marketId: number;
  url?: string;
}

/**
 * Renders nothing. Bridges the Centrifugo odds stream to the store: it builds a
 * channel per bookmaker returned for this market and, on each publication, writes
 * the new price back into the matching odd via the `updateOdd` store action.
 *
 * Lives inside the store tree so the subscription set tracks the bookmakers the
 * API actually returned.
 */
const OddsStreamController = ({ enabled, marketId, url }: OddsStreamControllerProps): JSX.Element | null => {
  const { bookmakers, updateOdd } = useSelector(
    store => ({
      bookmakers: store.bookmakers,
      updateOdd: store.updatePrice,
    }),
    shallow,
  );

  const channels = useMemo(
    () => (bookmakers ?? []).map(bookmaker => `market:${marketId}:bookmaker:${bookmaker.code}`),
    [bookmakers, marketId],
  );

  const onUpdate = useCallback(
    (update: OddsStreamUpdate) => {
      updateOdd(update.ocBetId, update.bookieCode, {
        american: update.oddsAmerican,
        decimal: update.oddsDig,
        fractional: update.odds,
        movement: update.movement,
        selectionId: update.selectionId,
        status: update.failed ? 'FAILED' : 'ACTIVE',
      });
    },
    [updateOdd],
  );

  useOddsStream({ channels, enabled, onUpdate, url });

  return null;
};

export default OddsStreamController;
